import {fromJS, List} from "immutable";
import React, {useEffect} from "react";
import {useMutation} from "react-apollo";
import {useDispatch, useSelector} from "react-redux";
import {loadPackagesData, updatePackages} from "../actions/WarehousePackageActions";
import Button from "../components/Button";
import StatusTag from "../components/StatusTag";
import {isJobLate} from "../dashboard/views/warehouses/WarehouseDashboard";
import {logout} from "../lib/api/AuthApi";
import {CONFIRM_CANCELLATION} from "../queries/warehouseJobs";
import {getAccessibleWarehouseIds} from "../reducers/authReducer";
import {getAllWarehouses} from "./Warehouses";


// Package states for jobs' packages.
export const PACKAGE_STATES = {
  DUE_TO_PROCESS: "idle",
  PICK: "picking",
  PACK: "packing",
  PICKUP_READY: "packed",
  IN_TRANSIT: "loaded",
  FULFILLED: "fulfilled", // Not displayed in dashboard lanes
  CANCELLED: "cancelled", // Not displayed in dashboard lanes
}

export function getWarehouseLabelFromId(warehouseId) {
  const warehouse = getAllWarehouses().find((w) => w.get("id") === warehouseId);
  return warehouse ? warehouse.get("shortLabel", warehouse.get("shortLabel", "--")) : "--";
}
export function getWarehouseIdFromParam(warehouse) {
  const match = warehouse.match(/warehouse:(.*)/);
  const warehouseId = match && match.length === 2 && match[1];

  // Return id only if valid
  return getAllWarehouses().find((w) => w.get("id") === warehouseId) ? warehouseId : null;
}

export function arePackagesWaitingForServerAck(packages) {
  return packages.some((p) => p.get("waitingForServerAck") === true);
}

export const getActionsForOrder = (state) => {
  const actions = [];

  switch (state) {
    case PACKAGE_STATES.DUE_TO_PROCESS:
      actions.push();
      break;
    default:
  }
}

// We use uids for packages as their ids are relative to the order, and
// always "001", "002", ...
export const getPackageUID = (pack) => {
  return `${pack.get("jobId")}-${pack.get("id")}`;
}
export const filterPackagesWithOrderId = (packages, jobId) => (
  !packages ? List() : packages.filter((p) => p.get("jobId") === jobId)
);

export function getAlertInfoForJob(job, jobPackages, mutations) {
  // Only cancellation is handled for now
  if (!job || job.cancelled !== "yes") {
    return null;
  }

  const inCancelledLane = jobPackages.every((p) => p.get("state") === PACKAGE_STATES.CANCELLED);
  if (inCancelledLane) {
    return null;
  }

  return {
    text: "Order has been cancelled",
    button: <Button onClick={() => mutations.onCancelOrder(job, jobPackages)} label="Remove Order" />
  };
}
export function getTagsForJob(job, jobPackages) {
  const tags = [];

  const someInIdleLane = jobPackages.some((p) => p.get("state") === PACKAGE_STATES.DUE_TO_PROCESS);
  
  if (someInIdleLane) {
    const duePreping = "Prep Date " + formatJobDate(job.pickupDate);
    tags.push(<StatusTag key="preping" color={StatusTag.COLORS.OUTLINE} text={duePreping} noMargin />);
  }

  const duePickup = "DUE PICKUP " + formatJobDate(job.pickupDate);
  tags.push(<StatusTag key="pickup" color={StatusTag.COLORS.GREY} text={duePickup} noMargin />);
  
  // Late or on schedule?
  if (isJobLate(job.pickupDate)) {
    tags.push(<StatusTag key="late" color={StatusTag.COLORS.ORANGE} text="Late" noMargin />);
  } else if (someInIdleLane) {
    // Not sure if "on schedule" should be shown outside of idle lane
    tags.push(<StatusTag key="onSchedule" color={StatusTag.COLORS.GREEN} text="On Schedule" noMargin />);
  }

  // Partial?
  const firstPackState = jobPackages.getIn([0, "state"]);
  const partialJob = jobPackages.some((p) => p.get("state") !== firstPackState);
  if (partialJob) {
    tags.push(<StatusTag key="partial" color={StatusTag.COLORS.RED} text="Partial" noMargin />);
  }

  return tags;
}

function formatJobDate(dateStr) {
  if (!dateStr) return null;

  const date = new Date(dateStr);

  return date.toLocaleString('en-us', { month: 'short', day: "2-digit" });
}


export function withWarehousePackages(WrappedComponent) {
  return function (props) {
    const dispatch = useDispatch();
    const warehouseId = props.warehouseId || getWarehouseIdFromParam(props.warehouse);
    const authenticated = useSelector(state => state.getIn(["auth", "authenticated"]));
    const accessibleWarehouseIds = useSelector(state => getAccessibleWarehouseIds(state.get("auth")));

    const status = useSelector(state => state.getIn(["warehousePackages", "status"]));
    const jobs = useSelector(state => state.getIn(["warehousePackages", "jobs", props.warehouseId]));
    const packages = useSelector(state => state.getIn(["warehousePackages", "packages", warehouseId]));

    useEffect(() => {
      if (status === "not_loaded" && props.data) {
        const dashboardJobs = props.data.all_active_jobs;
        const listingJobs = props.data.started_jobs && props.data.started_jobs.entries;
        const jobs = dashboardJobs || listingJobs;

        if (jobs) {
          dispatch(loadPackagesData(warehouseId, jobs));
        }
      }
    }, [status, props.data]);

    if (authenticated && !accessibleWarehouseIds.contains(warehouseId)) {
      // This user doesn't have the right to see this warehouse
      logout();
    }

    const getJobById = (jobId) => {
      if (!jobs || !jobs.size) {
        console.log("No jobs found for warehouse ", warehouseId);
        return null;
      }

      const job = jobs.get(jobId).toJS();
      if (!job) {
        console.log("No job found for this job id ", jobId);
      }

      return job;
    }

    const [confirmJobCancellation] = useMutation(CONFIRM_CANCELLATION);

    const alertMutations = {
      onCancelOrder: (job, jobPackages) => {
        // Send ack to server
        const mutationVariables = {
          variables: {
            data: {
              warehouseId: warehouseId,
              jobId: job.jobId,
            }
          }
        };
        confirmJobCancellation(mutationVariables);
    
        // Locally hide them
        const cancelledPackagesUIds = jobPackages.map((p) => getPackageUID(p));
        job.cancelled = "confirmed";
        dispatch(updatePackages(warehouseId, cancelledPackagesUIds, (p) => p.set("state", PACKAGE_STATES.CANCELLED)));
      }
    }

    return (
      <WrappedComponent
        warehousePackages={packages}
        warehousePackagesLoading={status === "loading" || status === "not_loaded"}
        warehousePackagesLoaded={status === "loaded"}
        warehousePackagesError={status === "error"}
        {...props}
        warehouseId={warehouseId}
        getJobById={getJobById}
        getAlertInfoForJobId={(id) => getAlertInfoForJob(getJobById(id), filterPackagesWithOrderId(packages, id), alertMutations)}
        getTagsForJobId={(id) => getTagsForJob(getJobById(id), filterPackagesWithOrderId(packages, id))}
        />
    )
  }
}
